import React from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeftIcon, EditIcon, PackageIcon, TagIcon, DollarSignIcon, CheckCircleIcon, AlertCircleIcon } from 'lucide-react';
// Shared mock — would normally come from a store / API
const mockProducts: Record<string, {
  id: string;
  name: string;
  description: string;
  price: number;
  salePrice?: number;
  sku: string;
  stockQuantity: number;
  category: string;
  imageUrl: string;
  isActive: boolean;
  options: {
    name: string;
    values: string[];
  }[];
  modifiers: {
    name: string;
    price: number;
  }[];
}> = {
  prod1: {
    id: 'prod1',
    name: 'Beach Towel',
    description: 'Soft cotton beach towel — 80x160 cm',
    price: 29.99,
    sku: 'BT-001',
    stockQuantity: 45,
    category: 'Accessories',
    imageUrl: 'https://images.unsplash.com/photo-1601060869389-3a4ee2f44a7e?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80',
    isActive: true,
    options: [{
      name: 'Color',
      values: ['Blue', 'Red', 'Sand']
    }],
    modifiers: [{
      name: 'Monogram embroidery',
      price: 4.5
    }, {
      name: 'Gift wrap',
      price: 2.0
    }]
  },
  prod3: {
    id: 'prod3',
    name: 'Swim Shorts',
    description: 'Quick-dry swim shorts for men, made from recycled polyester.',
    price: 34.99,
    salePrice: 29.99,
    sku: 'SS-003',
    stockQuantity: 28,
    category: 'Swimwear',
    imageUrl: 'https://images.unsplash.com/photo-1591047139829-d91aecb6caea?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80',
    isActive: true,
    options: [{
      name: 'Size',
      values: ['S', 'M', 'L', 'XL']
    }, {
      name: 'Color',
      values: ['Navy', 'Coral', 'Black']
    }],
    modifiers: [{
      name: 'Express delivery',
      price: 5.0
    }]
  }
};
export function ProductDetail() {
  const navigate = useNavigate();
  const {
    productId
  } = useParams<{
    productId: string;
  }>();
  const [params] = useSearchParams();
  const shopId = params.get('shop') || '';
  const product = productId ? mockProducts[productId] : null;
  if (!product) {
    return <div className="max-w-3xl mx-auto py-10 text-center" data-id="element-12122">
        <p className="text-sm text-gray-500" data-id="element-12123">Product not found.</p>
        <button onClick={() => navigate(-1)} className="mt-4 inline-flex items-center text-sm text-[#E2231A] hover:text-[#C41B14]" data-id="element-12124">
          <ArrowLeftIcon size={14} className="mr-1.5" data-id="element-12125" />
          Go back
        </button>
      </div>;
  }
  return <div className="max-w-[1200px] mx-auto space-y-6" data-id="element-12126">
      <div data-id="element-12127">
        <button onClick={() => navigate(shopId ? `/shop-details?id=${shopId}` : '/shops')} className="inline-flex items-center text-sm text-gray-500 hover:text-gray-900 mb-3" data-id="element-12128">
          <ArrowLeftIcon size={14} className="mr-1.5" data-id="element-12129" />
          Back to shop
        </button>
        <div className="flex items-start justify-between" data-id="element-12130">
          <div data-id="element-12131">
            <h1 className="text-2xl font-semibold text-gray-900" data-id="element-12132">
              {product.name}
            </h1>
            <p className="text-sm text-gray-500 mt-1" data-id="element-12133">SKU {product.sku}</p>
          </div>
          <button onClick={() => navigate(`/product-edit/${product.id}?shop=${shopId}`)} className="inline-flex items-center gap-2 px-4 py-2 bg-[#E2231A] hover:bg-[#C41B14] text-white text-sm font-medium rounded-lg shadow-sm" data-id="element-12134">
            <EditIcon size={14} data-id="element-12135" />
            Edit product
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-12136">
        {/* Image */}
        <div className="lg:col-span-1" data-id="element-12137">
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-id="element-12138">
            <div className="aspect-square bg-gray-100" data-id="element-12139">
              <img src={product.imageUrl} alt={product.name} className="w-full h-full object-cover" data-id="element-12140" />
            </div>
          </div>
        </div>

        {/* Details */}
        <div className="lg:col-span-2 space-y-6" data-id="element-12141">
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-id="element-12142">
            <div className="px-6 py-4 border-b border-gray-100" data-id="element-12143">
              <h2 className="text-sm font-semibold text-gray-900" data-id="element-12144">Overview</h2>
            </div>
            <div className="px-6 py-5 space-y-4" data-id="element-12145">
              <p className="text-sm text-gray-700 leading-relaxed" data-id="element-12146">
                {product.description}
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4" data-id="element-12147">
                <div data-id="element-12148">
                  <p className="text-xs text-gray-500 uppercase tracking-wider" data-id="element-12149">
                    Price
                  </p>
                  <p className="mt-1 text-base font-semibold text-gray-900" data-id="element-12150">
                    MYR {product.price.toFixed(2)}
                  </p>
                </div>
                {product.salePrice != null && <div data-id="element-12151">
                    <p className="text-xs text-gray-500 uppercase tracking-wider" data-id="element-12152">
                      Sale price
                    </p>
                    <p className="mt-1 text-base font-semibold text-red-600" data-id="element-12153">
                      MYR {product.salePrice.toFixed(2)}
                    </p>
                  </div>}
                <div data-id="element-12154">
                  <p className="text-xs text-gray-500 uppercase tracking-wider" data-id="element-12155">
                    In stock
                  </p>
                  <p className="mt-1 text-base font-semibold text-gray-900" data-id="element-12156">
                    {product.stockQuantity}
                  </p>
                </div>
                <div data-id="element-12157">
                  <p className="text-xs text-gray-500 uppercase tracking-wider" data-id="element-12158">
                    Category
                  </p>
                  <p className="mt-1 text-base font-semibold text-gray-900" data-id="element-12159">
                    {product.category}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2" data-id="element-12160">
                {product.isActive ? <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md bg-green-50 border border-green-200 text-green-700 text-xs font-medium" data-id="element-12161">
                    <CheckCircleIcon size={12} data-id="element-12162" /> Active
                  </span> : <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md bg-gray-100 border border-gray-200 text-gray-600 text-xs font-medium" data-id="element-12163">
                    <AlertCircleIcon size={12} data-id="element-12164" /> Inactive
                  </span>}
              </div>
            </div>
          </div>

          {/* Options */}
          {product.options.length > 0 && <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-id="element-12165">
              <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-2" data-id="element-12166">
                <TagIcon size={16} className="text-gray-500" data-id="element-12167" />
                <h2 className="text-sm font-semibold text-gray-900" data-id="element-12168">Options</h2>
              </div>
              <div className="px-6 py-5 space-y-3" data-id="element-12169">
                {product.options.map(opt => <div key={opt.name} data-id="element-12170">
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1.5" data-id="element-12171">
                      {opt.name}
                    </p>
                    <div className="flex flex-wrap gap-1.5" data-id="element-12172">
                      {opt.values.map(v => <span key={v} className="inline-flex items-center px-2.5 py-1 rounded-md border border-gray-200 bg-gray-50 text-sm text-gray-700" data-id="element-12173">
                          {v}
                        </span>)}
                    </div>
                  </div>)}
              </div>
            </div>}

          {/* Modifiers */}
          {product.modifiers.length > 0 && <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-id="element-12174">
              <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-2" data-id="element-12175">
                <PackageIcon size={16} className="text-gray-500" data-id="element-12176" />
                <h2 className="text-sm font-semibold text-gray-900" data-id="element-12177">
                  Modifiers
                </h2>
              </div>
              <div className="divide-y divide-gray-100" data-id="element-12178">
                {product.modifiers.map(m => <div key={m.name} className="px-6 py-3 flex items-center justify-between" data-id="element-12179">
                    <p className="text-sm text-gray-700" data-id="element-12180">{m.name}</p>
                    <p className="text-sm font-semibold text-gray-900" data-id="element-12181">
                      +MYR {m.price.toFixed(2)}
                    </p>
                  </div>)}
              </div>
            </div>}
        </div>
      </div>
    </div>;
}